import { canEnqueueOffline, offlineLessonOpen, OFFLINE_QUEUE_LIMIT, OFFLINE_MAX_MS } from "../../domain/offline";
import type { ItemAnswerKey, PlayItem } from "../play/types";

const DB_NAME = "clever-offline";
const DB_VERSION = 1;
const QUEUE = "attempt-queue";
const LESSONS = "lessons";

export interface QueuedAttempt {
  id: string;
  sessionId: string;
  learnerKey: string;
  itemId: string;
  selected: unknown;
  clientSeq: number;
  createdAt: number;
}

export interface LessonSnapshot {
  slug: string;
  sessionId: string;
  learnerKey: string;
  items: PlayItem[];
  answerKeys?: Record<string, ItemAnswerKey>;
  startedAt: number;
  entitlementExpiresAt: number | null;
  lastIndex: number;
  completed?: boolean;
}

function clientUuidV7(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  const ts = Date.now();
  bytes[0] = (ts / 2 ** 40) & 0xff;
  bytes[1] = (ts / 2 ** 32) & 0xff;
  bytes[2] = (ts / 2 ** 24) & 0xff;
  bytes[3] = (ts / 2 ** 16) & 0xff;
  bytes[4] = (ts / 2 ** 8) & 0xff;
  bytes[5] = ts & 0xff;
  bytes[6] = (bytes[6] & 0x0f) | 0x70;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = [...bytes].map((b) => b.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(QUEUE)) {
        const store = db.createObjectStore(QUEUE, { keyPath: "id" });
        store.createIndex("sessionId", "sessionId");
      }
      if (!db.objectStoreNames.contains(LESSONS)) {
        db.createObjectStore(LESSONS, { keyPath: "slug" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function reqTo<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function loadLessonSnapshot(slug: string): Promise<LessonSnapshot | null> {
  if (typeof indexedDB === "undefined") return null;
  const db = await openDb();
  try {
    const row = await reqTo(db.transaction(LESSONS).objectStore(LESSONS).get(slug));
    return (row as LessonSnapshot) ?? null;
  } finally {
    db.close();
  }
}

export async function saveLessonSnapshot(snap: LessonSnapshot): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  const db = await openDb();
  try {
    await reqTo(db.transaction(LESSONS, "readwrite").objectStore(LESSONS).put(snap));
  } finally {
    db.close();
  }
}

export async function listQueuedAttempts(sessionId: string): Promise<QueuedAttempt[]> {
  if (typeof indexedDB === "undefined") return [];
  const db = await openDb();
  try {
    const idx = db.transaction(QUEUE).objectStore(QUEUE).index("sessionId");
    const rows = await reqTo(idx.getAll(sessionId));
    return (rows as QueuedAttempt[]).sort((a, b) => a.createdAt - b.createdAt || a.clientSeq - b.clientSeq);
  } finally {
    db.close();
  }
}

export async function enqueueAttempt(input: Omit<QueuedAttempt, "id" | "createdAt">): Promise<QueuedAttempt | null> {
  if (typeof indexedDB === "undefined") return null;
  const existing = await listQueuedAttempts(input.sessionId);
  if (!canEnqueueOffline(existing.length)) return null;
  const row: QueuedAttempt = { ...input, id: clientUuidV7(), createdAt: Date.now() };
  const db = await openDb();
  try {
    await reqTo(db.transaction(QUEUE, "readwrite").objectStore(QUEUE).put(row));
  } finally {
    db.close();
  }
  return row;
}

export async function removeQueuedAttempt(id: string): Promise<void> {
  if (typeof indexedDB === "undefined") return;
  const db = await openDb();
  try {
    await reqTo(db.transaction(QUEUE, "readwrite").objectStore(QUEUE).delete(id));
  } finally {
    db.close();
  }
}

export function snapshotStillOpen(snap: LessonSnapshot, now = Date.now()): boolean {
  return offlineLessonOpen({
    startedAt: snap.startedAt,
    now,
    entitlementExpiresAt: snap.entitlementExpiresAt,
    completed: snap.completed,
  });
}

export { OFFLINE_QUEUE_LIMIT, OFFLINE_MAX_MS, canEnqueueOffline, offlineLessonOpen };
